import type { StatsResponse } from "@cdt/shared";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ClipboardList,
  Mail,
  Star,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface KpiCardsProps {
  stats: StatsResponse;
}

type Tint = "blue" | "emerald" | "amber" | "red" | "slate";

interface Item {
  key: string;
  label: string;
  value: number | string;
  hint: string;
  icon: LucideIcon;
  tint: Tint;
}

const TINT: Record<Tint, { chip: string; bar: string }> = {
  blue: { chip: "bg-blue-50 text-blue-600 ring-blue-100", bar: "bg-blue-500" },
  emerald: {
    chip: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    bar: "bg-emerald-500",
  },
  amber: {
    chip: "bg-amber-50 text-amber-600 ring-amber-100",
    bar: "bg-amber-500",
  },
  red: { chip: "bg-red-50 text-red-600 ring-red-100", bar: "bg-red-500" },
  slate: {
    chip: "bg-slate-100 text-slate-500 ring-slate-200",
    bar: "bg-slate-400",
  },
};

export function KpiCards({ stats }: KpiCardsProps) {
  const items: Item[] = [
    {
      key: "processed24h",
      label: "Processati 24h",
      value: stats.last24h.processed,
      hint: "Volume elaborato nell'ultimo giorno",
      icon: Mail,
      tint: "blue",
    },
    {
      key: "created",
      label: "Creati",
      value: stats.totals.CREATED,
      hint: "Eventi creati automaticamente",
      icon: CheckCircle2,
      tint: "emerald",
    },
    {
      key: "manualReview",
      label: "Da revisionare",
      value: stats.manualReviewOpen,
      hint: "In coda revisione manuale",
      icon: ClipboardList,
      tint: "amber",
    },
    {
      key: "errors",
      label: "Errori aperti",
      value: stats.errorOpen,
      hint: "Eventi che richiedono retry",
      icon: AlertTriangle,
      tint: "red",
    },
    {
      key: "ignored",
      label: "Scartati",
      value: stats.totals.IGNORED,
      hint: "Email ignorate perché non rilevanti",
      icon: Ban,
      tint: "slate",
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {items.map((item) => {
        const Icon = item.icon;
        const tint = TINT[item.tint];
        return (
          <Card key={item.key} className="overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  {item.label}
                </span>
                <span
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-xl ring-1",
                    tint.chip,
                  )}
                >
                  <Icon className="h-[18px] w-[18px]" />
                </span>
              </div>
              <div className="mt-3 text-4xl font-semibold tabular-nums tracking-tight text-slate-900">
                {item.value}
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                {item.hint}
              </p>
            </CardContent>
            <div className={cn("h-1 w-full", tint.bar)} />
          </Card>
        );
      })}
    </div>
  );
}

function formatCycleDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("it-CH", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function RuntimeStatusPill({ health }: { health: StatsResponse["health"] }) {
  const ok = health.status === "ok";
  return (
    <div className="glass-card flex items-center gap-3 rounded-full px-4 py-2">
      <span className="relative flex h-2.5 w-2.5">
        {ok ? (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
        ) : null}
        <span
          className={cn(
            "relative inline-flex h-2.5 w-2.5 rounded-full",
            ok ? "bg-emerald-500" : "bg-amber-500",
          )}
        />
      </span>
      <span className="text-sm font-semibold text-slate-900">
        {ok ? "Sistema operativo" : "Degradato"}
      </span>
      <span className="hidden h-4 w-px bg-slate-200 sm:block" />
      <span className="hidden text-xs text-muted-foreground sm:inline">
        Ultimo ciclo {formatCycleDate(health.lastCycleAt)}
      </span>
      <span className="hidden text-xs text-muted-foreground md:inline">
        · Uptime {Math.floor(health.uptime / 60)} min
      </span>
    </div>
  );
}

export function PriorityPanel({
  priorityStats,
}: {
  priorityStats: StatsResponse["priorityStats"];
}) {
  const dist = priorityStats.distribution;
  const rows = [
    { key: "unrated", label: "Non valutati", value: dist.unrated, accent: "bg-slate-300", text: "text-slate-500" },
    { key: "p1", label: "★", value: dist.p1, accent: "bg-amber-300", text: "text-amber-500" },
    { key: "p2", label: "★★", value: dist.p2, accent: "bg-amber-400", text: "text-amber-500" },
    { key: "p3", label: "★★★", value: dist.p3, accent: "bg-amber-500", text: "text-amber-600" },
  ];
  const max = Math.max(1, ...rows.map((r) => r.value));

  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100/80 px-5 py-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-500 ring-1 ring-amber-100">
            <Star className="h-4 w-4" />
          </span>
          <h3 className="text-sm font-semibold text-slate-900">Priorità eventi</h3>
        </div>
        {priorityStats.unrated > 0 ? (
          <Link
            to="/priority-review"
            className="rounded-md text-xs font-medium text-blue-600 underline-offset-2 hover:underline"
          >
            {priorityStats.unrated} da valutare →
          </Link>
        ) : null}
      </div>
      <CardContent className="flex flex-1 flex-col justify-center gap-4 p-5">
        {rows.map((row) => (
          <div key={row.key} className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className={cn("font-medium tabular-nums", row.text)}>
                {row.label}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">
                {row.value}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div
                className={cn("h-full rounded-full", row.accent)}
                style={{ width: `${Math.round((row.value / max) * 100)}%` }}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
